import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  PRACTICE_KIND_LIST,
  PRACTICE_KINDS,
  type PracticeKindId,
} from '@/practice/registry';

interface AddPracticeItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonId: string;
  orgId: string;
  enabledKinds: PracticeKindId[];
  nextPosition: number;
  onAdded: () => void;
}

interface ContentOption {
  id: string;
  label: string;
}

export default function AddPracticeItemDialog({
  open,
  onOpenChange,
  lessonId,
  orgId: _orgId,
  enabledKinds,
  nextPosition,
  onAdded,
}: AddPracticeItemDialogProps) {
  const { user } = useAuth();
  const [kindId, setKindId] = useState<PracticeKindId | ''>('');
  const [contentId, setContentId] = useState<string>('');
  const [grade, setGrade] = useState<string>('');
  const [options, setOptions] = useState<ContentOption[]>([]);
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [saving, setSaving] = useState(false);

  const availableKinds = useMemo(
    () => PRACTICE_KIND_LIST.filter((k) => enabledKinds.includes(k.id)),
    [enabledKinds],
  );

  useEffect(() => {
    if (!open) {
      setKindId('');
      setContentId('');
      setGrade('');
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (!kindId || !user) return;
    setContentId('');
    setOptions([]);
    if (kindId === 'writing_prompt') return;

    setLoadingOpts(true);
    (async () => {
      try {
        if (kindId === 'question_set') {
          const { data, error } = await supabase
            .from('instructor_tests')
            .select('id, title, grade, subject')
            .eq('instructor_id', user.id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(200);
          if (error) throw error;
          setOptions(
            (data ?? []).map((t: any) => ({
              id: t.id,
              label: `${t.title} (G${t.grade} · ${t.subject})`,
            })),
          );
        } else if (kindId === 'game') {
          const { data, error } = await supabase
            .from('games')
            .select('id, name, game_type')
            .eq('is_active', true)
            .order('name', { ascending: true })
            .limit(200);
          if (error) throw error;
          setOptions(
            (data ?? []).map((g: any) => ({ id: g.id, label: `${g.name} (${g.game_type})` })),
          );
        } else if (kindId === 'worksheet') {
          // Worksheets are generated dynamically; no central table to pick from yet.
          setOptions([]);
        }
      } catch (err: any) {
        toast.error(err.message ?? 'Failed to load content');
      } finally {
        setLoadingOpts(false);
      }
    })();
  }, [kindId, user]);

  const submit = async () => {
    if (!user || !kindId) return;
    const payload: Record<string, any> = {
      lesson_id: lessonId,
      kind_id: kindId,
      created_by: user.id,
      position: nextPosition,
    };

    if (kindId === 'question_set') {
      if (!contentId) return toast.error('Pick a test');
      payload.test_id = contentId;
    } else if (kindId === 'game') {
      if (!contentId) return toast.error('Pick a game');
      payload.game_id = contentId;
    } else if (kindId === 'worksheet') {
      return toast.error(
        'Worksheets are not yet selectable — we ship the picker once worksheets have a stored ID.',
      );
    } else if (kindId === 'writing_prompt') {
      // grade-only; no FK
    }

    setSaving(true);
    const { error } = await supabase
      .from('lesson_practice_items')
      .insert(payload as any);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Added to lesson');
    onAdded();
    onOpenChange(false);
  };

  const needsContent = kindId === 'question_set' || kindId === 'game';
  const isWorksheet = kindId === 'worksheet';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add practice item</DialogTitle>
          <DialogDescription>
            Pick a kind, then choose existing content from your organization.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Kind</Label>
            <Select
              value={kindId}
              onValueChange={(v) => setKindId(v as PracticeKindId)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a kind" />
              </SelectTrigger>
              <SelectContent>
                {availableKinds.map((k) => (
                  <SelectItem key={k.id} value={k.id}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableKinds.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                No practice kinds enabled for this organization.
              </p>
            )}
          </div>

          {needsContent && (
            <div>
              <Label>{PRACTICE_KINDS[kindId as PracticeKindId].label}</Label>
              {loadingOpts ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : (
                <Select value={contentId} onValueChange={setContentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick existing content" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        Nothing available — create one first.
                      </div>
                    ) : (
                      options.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {isWorksheet && (
            <p className="text-sm text-muted-foreground">
              Worksheets are currently generated on demand and don't have stored
              IDs yet — adding them to lessons will be enabled in a follow-up.
            </p>
          )}

          {kindId === 'writing_prompt' && (
            <div>
              <Label htmlFor="wp-grade">Grade hint (optional)</Label>
              <Input
                id="wp-grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                placeholder="Inherited from lesson if blank"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Opens the writing pad for this grade. (Stored as grade hint only.)
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              saving || !kindId || isWorksheet || (needsContent && !contentId)
            }
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
